import { SlotProps } from '@mui/material';
import IconButton from '@mui/material/IconButton';
import InputAdornment from '@mui/material/InputAdornment';
import TextField from '@mui/material/TextField';
import React from 'react';

import CopyIcon from '@/assets/icons/copy.svg?react';
import { useToast } from '@/hooks/useToast';

interface Props {
  readonly label?: string;
  readonly value: string;
  readonly disabled?: boolean;
  readonly fullWidth?: boolean;

  onChange?(value: string): void;
}

export const WalletAddressInput: React.FC<Props> = (props: Props): React.ReactElement => {
  const { label, value, disabled = false, fullWidth = true, onChange } = props;
  const toast = useToast();

  const onCopyAddress = React.useCallback((): void => {
    const { clipboard } = navigator;
    clipboard
      .writeText(value)
      .then((): void => {
        toast.show('Address copied successfully', 'success');
      })
      .catch(console.debug);
  }, [toast, value]);

  const handleChange = React.useCallback(
    (event: React.ChangeEvent<HTMLInputElement>): void => {
      if (onChange) {
        onChange(event.target.value);
      }
    },
    [onChange],
  );

  React.useEffect((): VoidFunction => {
    return (): void => {
      toast.remove();
    };
  }, [toast]);

  const addressSlotProps = React.useMemo((): SlotProps<any, any, any> => {
    return {
      input: {
        endAdornment: (
          <InputAdornment position="end">
            <IconButton color="inherit" onClick={onCopyAddress} disabled={!value}>
              <CopyIcon fill="currentColor" />
            </IconButton>
          </InputAdornment>
        ),
      },
    };
  }, [onCopyAddress, value]);

  return (
    <TextField
      label={label}
      value={value}
      onChange={handleChange}
      disabled={disabled}
      fullWidth={fullWidth}
      slotProps={addressSlotProps}
    />
  );
};
